<template>
  <div v-if="m" class="space-y-4">
    <!-- Step 0: Data Source -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-2 border-b border-gray-200 bg-gray-800 text-white flex justify-between items-center">
        <span class="text-xs font-mono font-semibold">STEP 0: DATA SOURCE</span>
        <span v-if="m.data_source.fallback" class="text-xs bg-amber-500 text-white px-2 py-0.5 rounded">FALLBACK</span>
      </div>
      <div class="p-4 font-mono text-sm space-y-1">
        <div class="flex gap-8">
          <div><span class="text-gray-500">Requested:</span> {{ m.data_source.methodology_requested }} / {{ m.data_source.alignment_requested }}</div>
          <div><span class="text-gray-500">Used:</span> {{ m.data_source.methodology_used }} / {{ m.data_source.alignment_used }}</div>
          <div><span class="text-gray-500">Year:</span> {{ m.data_source.year }}</div>
        </div>
        <div class="text-gray-500">Source: KAIRON_EDW.GROWTH.PROVIDER_METRICS_WIDE</div>
      </div>
    </div>

    <!-- Step 1: Roster Aggregation -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-2 border-b border-gray-200 bg-gray-800 text-white">
        <span class="text-xs font-mono font-semibold">STEP 1: ROSTER AGGREGATION</span>
      </div>
      <div class="p-4 font-mono text-sm">
        <div class="text-xs text-gray-500 mb-2">SUM(attrib_benes * metric) / SUM(attrib_benes) — bene-weighted average per NPI</div>
        <table class="w-full">
          <tbody>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 w-48">TINs</td><td class="py-1.5 font-semibold">{{ projection.roster?.entity_count }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">NPIs (with attribution)</td><td class="py-1.5 font-semibold">{{ fmtNum(projection.roster?.npi_count) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Total Benes</td><td class="py-1.5 font-semibold">{{ projection.roster?.total_benes?.toLocaleString() }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Weighted PMPY</td><td class="py-1.5 font-semibold">{{ fmtExact(projection.performance?.weighted_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 pl-4">├ IP</td><td class="py-1.5">{{ fmtExact(projection.performance?.weighted_ip_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 pl-4">├ SNF</td><td class="py-1.5">{{ fmtExact(projection.performance?.weighted_snf_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 pl-4">├ OP</td><td class="py-1.5">{{ fmtExact(projection.performance?.weighted_op_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 pl-4">├ PB</td><td class="py-1.5">{{ fmtExact(projection.performance?.weighted_pb_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 pl-4">├ HHA</td><td class="py-1.5">{{ fmtExact(projection.performance?.weighted_hha_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 pl-4">├ HS</td><td class="py-1.5">{{ fmtExact(projection.performance?.weighted_hs_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 pl-4">└ DME</td><td class="py-1.5">{{ fmtExact(projection.performance?.weighted_dme_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Weighted HCC</td><td class="py-1.5 font-semibold">{{ projection.performance?.weighted_hcc }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Weighted AWV Rate</td><td class="py-1.5 font-semibold">{{ fmtPct(projection.performance?.weighted_awv) }}</td></tr>
            <tr><td class="py-1.5 text-gray-500">Total Expenditure</td><td class="py-1.5 font-semibold">{{ fmtExact(projection.performance?.total_expenditure) }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Step 2: Historical Benchmark -->
    <div v-if="m.historical_benchmark" class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-2 border-b border-gray-200 bg-gray-800 text-white">
        <span class="text-xs font-mono font-semibold">STEP 2: HISTORICAL BENCHMARK</span>
      </div>
      <div class="p-4 font-mono text-sm">
        <div class="text-xs text-gray-500 mb-2">Source: TIN's own BY1/BY2/BY3 PMPY + county FFS regional adjustment</div>

        <!-- BY year table -->
        <table class="w-full mb-4">
          <thead>
            <tr class="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
              <th class="pb-1.5 pr-4">Year</th>
              <th class="pb-1.5 pr-4 text-right">Raw PMPY</th>
              <th class="pb-1.5 pr-4 text-right">Trended</th>
              <th class="pb-1.5 pr-4 text-right">Risk-Adj</th>
              <th class="pb-1.5 pr-4 text-right">HCC</th>
              <th class="pb-1.5 pr-4 text-right">Benes</th>
              <th class="pb-1.5 text-right">Weight</th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-b border-gray-200">
              <td class="py-1.5 pr-4 text-gray-500">BY1 ({{ m.historical_benchmark.by1_pmpy > 0 ? 'data' : 'no data' }})</td>
              <td class="py-1.5 pr-4 text-right">${{ fmtExact(m.historical_benchmark.by1_pmpy) }}</td>
              <td class="py-1.5 pr-4 text-right">${{ fmtExact(m.historical_benchmark.by1_trended) }}</td>
              <td class="py-1.5 pr-4 text-right">${{ fmtExact(m.historical_benchmark.by1_risk_adjusted) }}</td>
              <td class="py-1.5 pr-4 text-right">{{ m.historical_benchmark.by1_hcc }}</td>
              <td class="py-1.5 pr-4 text-right">{{ m.historical_benchmark.by1_benes?.toLocaleString() }}</td>
              <td class="py-1.5 text-right">10%</td>
            </tr>
            <tr class="border-b border-gray-200">
              <td class="py-1.5 pr-4 text-gray-500">BY2</td>
              <td class="py-1.5 pr-4 text-right">${{ fmtExact(m.historical_benchmark.by2_pmpy) }}</td>
              <td class="py-1.5 pr-4 text-right">${{ fmtExact(m.historical_benchmark.by2_trended) }}</td>
              <td class="py-1.5 pr-4 text-right">${{ fmtExact(m.historical_benchmark.by2_risk_adjusted) }}</td>
              <td class="py-1.5 pr-4 text-right">{{ m.historical_benchmark.by2_hcc }}</td>
              <td class="py-1.5 pr-4 text-right">{{ m.historical_benchmark.by2_benes?.toLocaleString() }}</td>
              <td class="py-1.5 text-right">30%</td>
            </tr>
            <tr class="border-b border-gray-200 bg-gray-50">
              <td class="py-1.5 pr-4 font-semibold">BY3</td>
              <td class="py-1.5 pr-4 text-right font-semibold">${{ fmtExact(m.historical_benchmark.by3_pmpy) }}</td>
              <td class="py-1.5 pr-4 text-right text-gray-500">—</td>
              <td class="py-1.5 pr-4 text-right text-gray-500">—</td>
              <td class="py-1.5 pr-4 text-right">{{ m.historical_benchmark.by3_hcc }}</td>
              <td class="py-1.5 pr-4 text-right">{{ m.historical_benchmark.by3_benes?.toLocaleString() }}</td>
              <td class="py-1.5 text-right font-semibold">60%</td>
            </tr>
          </tbody>
        </table>

        <!-- Benchmark assembly -->
        <table class="w-full">
          <tbody>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 w-48">Weighted Historical PMPY</td><td class="py-1.5 font-semibold">${{ fmtExact(m.historical_benchmark.weighted_benchmark_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Regional FFS PMPY</td><td class="py-1.5">${{ fmtExact(m.historical_benchmark.regional_ffs_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200">
              <td class="py-1.5 text-gray-500">Regional Adjustment</td>
              <td class="py-1.5" :class="m.historical_benchmark.regional_adjustment >= 0 ? 'text-emerald-600' : 'text-red-500'">
                {{ m.historical_benchmark.regional_adjustment >= 0 ? '+' : '' }}${{ fmtExact(m.historical_benchmark.regional_adjustment) }}
                <span class="text-xs text-gray-500 ml-2">
                  ({{ m.historical_benchmark.regional_adjustment >= 0 ? '35% weight — historical below regional' : '15% weight — historical above regional' }})
                </span>
              </td>
            </tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Years Available</td><td class="py-1.5">{{ m.historical_benchmark.years_available }}/3</td></tr>
            <tr><td class="py-1.5 text-gray-500 font-semibold">Final Benchmark PMPY</td><td class="py-1.5 font-bold text-lg">${{ fmtExact(m.historical_benchmark.final_benchmark_pmpy) }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Step 2: Regional FFS Detail -->
    <div v-if="m.regional_ffs" class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-2 border-b border-gray-200 bg-gray-800 text-white">
        <span class="text-xs font-mono font-semibold">STEP 2 (DETAIL): REGIONAL FFS BY ENROLLMENT TYPE</span>
      </div>
      <div class="p-4 font-mono text-sm">
        <div class="text-xs text-gray-500 mb-2">Source: MSSP_COUNTY_FFS + BILLING_ENTITY_COUNTY_DISTRIBUTION ({{ m.regional_ffs?.county_count }} counties)</div>

        <!-- Enrollment mix bar -->
        <div v-if="projection.benchmark?.enrollment_mix" class="mb-4">
          <div class="flex gap-1 h-6 rounded overflow-hidden mb-2">
            <div class="bg-red-400 flex items-center justify-center text-white text-xs" :style="{ width: (projection.benchmark.enrollment_mix.pct_esrd * 100) + '%' }"></div>
            <div class="bg-amber-400 flex items-center justify-center text-white text-xs" :style="{ width: (projection.benchmark.enrollment_mix.pct_disabled * 100) + '%' }">{{ fmtPct(projection.benchmark.enrollment_mix.pct_disabled) }}</div>
            <div class="bg-blue-400 flex items-center justify-center text-white text-xs" :style="{ width: (projection.benchmark.enrollment_mix.pct_aged_dual * 100) + '%' }">{{ fmtPct(projection.benchmark.enrollment_mix.pct_aged_dual) }}</div>
            <div class="bg-emerald-400 flex items-center justify-center text-white text-xs" :style="{ width: (projection.benchmark.enrollment_mix.pct_aged_nondual * 100) + '%' }">{{ fmtPct(projection.benchmark.enrollment_mix.pct_aged_nondual) }}</div>
          </div>
          <div class="flex gap-4 text-xs">
            <span><span class="inline-block w-2 h-2 rounded bg-red-400 mr-1"></span>ESRD {{ fmtPct(projection.benchmark.enrollment_mix.pct_esrd) }}</span>
            <span><span class="inline-block w-2 h-2 rounded bg-amber-400 mr-1"></span>Disabled {{ fmtPct(projection.benchmark.enrollment_mix.pct_disabled) }}</span>
            <span><span class="inline-block w-2 h-2 rounded bg-blue-400 mr-1"></span>Aged/Dual {{ fmtPct(projection.benchmark.enrollment_mix.pct_aged_dual) }}</span>
            <span><span class="inline-block w-2 h-2 rounded bg-emerald-400 mr-1"></span>Aged/Non-Dual {{ fmtPct(projection.benchmark.enrollment_mix.pct_aged_nondual) }}</span>
          </div>
        </div>

        <!-- Per-type benchmarks -->
        <table v-if="enrollmentTypes" class="w-full mb-4">
          <thead>
            <tr class="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
              <th class="pb-1.5 pr-4">Enrollment Type</th>
              <th class="pb-1.5 pr-4 text-right">Benchmark PMPY</th>
              <th class="pb-1.5 pr-4 text-right">Risk Score</th>
              <th class="pb-1.5 text-right">Est. Mix</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(data, type) in enrollmentTypes" :key="type" class="border-b border-gray-200">
              <td class="py-1.5 pr-4 text-gray-500">{{ type }}</td>
              <td class="py-1.5 pr-4 text-right">${{ fmtExact(data.benchmark_pmpy) }}</td>
              <td class="py-1.5 pr-4 text-right">{{ data.risk_score }}</td>
              <td class="py-1.5 text-right">{{ data.mix ? fmtPct(data.mix) : '—' }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="projection.benchmark?.blended_all_types_pmpy && projection.benchmark?.aged_nondual_only_pmpy" class="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 mb-3">
          Blended benchmark (${{ fmtExact(projection.benchmark.blended_all_types_pmpy) }}) is ${{ fmtExact(projection.benchmark.blended_all_types_pmpy - projection.benchmark.aged_nondual_only_pmpy) }} higher than aged-nondual-only (${{ fmtExact(projection.benchmark.aged_nondual_only_pmpy) }}).
        </div>

        <div class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          {{ m.regional_ffs?.benchmark_by_enrollment_type?.note || 'Benchmark uses historical expenditure + regional adjustment' }}
        </div>
        <div class="mt-3">
          <span class="text-gray-500">Benchmark Coverage:</span> {{ fmtPct(projection.benchmark?.benchmark_coverage) }} of benes have county-level benchmark data
        </div>
      </div>
    </div>

    <!-- Step 2e: Trend Forward -->
    <div v-if="m.trend_forward && m.trend_forward.years_forward > 0" class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-2 border-b border-gray-200 bg-gray-800 text-white">
        <span class="text-xs font-mono font-semibold">STEP 2E: TREND FORWARD ({{ m.trend_forward.data_year }} → PY{{ m.trend_forward.target_py }})</span>
      </div>
      <div class="p-4 font-mono text-sm">
        <div class="bg-gray-50 rounded p-3 mb-3 text-center">
          <div class="text-lg">
            ${{ fmtExact(m.trend_forward.benchmark_before) }}
            <span class="text-gray-500 mx-1">×</span>
            {{ m.trend_forward.cumulative_factor }}
            <span class="text-gray-500 mx-2">=</span>
            <span class="font-bold">${{ fmtExact(m.trend_forward.benchmark_after) }}</span>
          </div>
        </div>
        <table class="w-full">
          <tbody>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 w-48">Annual Growth Rate</td><td class="py-1.5">{{ (m.trend_forward.annual_growth_rate * 100).toFixed(1) }}%</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Years Forward</td><td class="py-1.5">{{ m.trend_forward.years_forward }}</td></tr>
            <tr><td class="py-1.5 text-gray-500">Basis</td><td class="py-1.5 text-xs">{{ m.trend_forward.growth_basis }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Step 3: Risk Adjustment -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-2 border-b border-gray-200 bg-gray-800 text-white">
        <span class="text-xs font-mono font-semibold">STEP 3: RISK ADJUSTMENT</span>
      </div>
      <div class="p-4 font-mono text-sm">
        <div class="bg-gray-50 rounded p-3 mb-4 text-center">
          <div class="text-xs text-gray-500 mb-1">{{ m.risk_adjustment.formula }}</div>
          <div class="text-lg">
            ${{ fmtExact(m.risk_adjustment.regional_benchmark_pmpy) }}
            <span class="text-gray-500 mx-1">×</span>
            ({{ m.risk_adjustment.roster_hcc }}
            <span class="text-gray-500 mx-1">÷</span>
            {{ m.risk_adjustment.regional_hcc }})
            <span class="text-gray-500 mx-2">=</span>
            <span class="font-bold">${{ fmtExact(m.risk_adjustment.risk_adjusted_benchmark_pmpy) }}</span>
          </div>
        </div>
        <table class="w-full">
          <tbody>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 w-48">Benchmark PMPY (input)</td><td class="py-1.5">${{ fmtExact(m.risk_adjustment.regional_benchmark_pmpy) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Roster HCC (PY)</td><td class="py-1.5">{{ m.risk_adjustment.roster_hcc }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Benchmark HCC (BY3)</td><td class="py-1.5">{{ m.risk_adjustment.regional_hcc }}</td></tr>
            <tr class="border-b border-gray-200">
              <td class="py-1.5 text-gray-500">Risk Ratio</td>
              <td class="py-1.5 font-semibold" :class="m.risk_adjustment.ratio < 1 ? 'text-amber-600' : ''">
                {{ m.risk_adjustment.ratio }}×
                <span v-if="m.risk_adjustment.ratio < 1" class="text-xs font-normal">(healthier than BY3 → benchmark reduced)</span>
                <span v-else class="text-xs font-normal text-gray-500">(sicker than BY3 → benchmark increased)</span>
              </td>
            </tr>
            <tr><td class="py-1.5 text-gray-500 font-semibold">Risk-Adjusted Benchmark</td><td class="py-1.5 font-bold">${{ fmtExact(m.risk_adjustment.risk_adjusted_benchmark_pmpy) }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Step 4: Savings Calculation -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-2 border-b border-gray-200 bg-gray-800 text-white">
        <span class="text-xs font-mono font-semibold">STEP 4: SAVINGS CALCULATION</span>
      </div>
      <div class="p-4 font-mono text-sm">
        <div class="bg-gray-50 rounded p-3 mb-4 text-center">
          <div class="text-xs text-gray-500 mb-1">{{ m.savings_calculation.formula }}</div>
          <div class="text-lg">
            (${{ fmtExact(m.savings_calculation.risk_adj_benchmark_pmpy) }}
            <span class="text-gray-500 mx-1">×</span>
            {{ m.savings_calculation.total_benes?.toLocaleString() }})
            <span class="text-gray-500 mx-1">−</span>
            ${{ fmtExact(m.savings_calculation.total_expenditure) }}
            <span class="text-gray-500 mx-2">=</span>
            <span class="font-bold" :class="m.savings_calculation.savings_amount >= 0 ? 'text-emerald-600' : 'text-red-500'">
              ${{ fmtExact(m.savings_calculation.savings_amount) }}
            </span>
          </div>
        </div>
        <table class="w-full">
          <tbody>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 w-48">Total Benchmark</td><td class="py-1.5">${{ fmtExact(m.savings_calculation.total_benchmark) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Total Expenditure</td><td class="py-1.5">${{ fmtExact(m.savings_calculation.total_expenditure) }}</td></tr>
            <tr class="border-b border-gray-200">
              <td class="py-1.5 text-gray-500">Savings Amount</td>
              <td class="py-1.5 font-semibold" :class="m.savings_calculation.savings_amount >= 0 ? 'text-emerald-600' : 'text-red-500'">
                ${{ fmtExact(m.savings_calculation.savings_amount) }}
              </td>
            </tr>
            <tr>
              <td class="py-1.5 text-gray-500">Savings %</td>
              <td class="py-1.5 font-semibold" :class="m.savings_calculation.savings_pct >= 0 ? 'text-emerald-600' : 'text-red-500'">
                {{ (m.savings_calculation.savings_pct * 100).toFixed(2) }}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Step 5: MSR -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-2 border-b border-gray-200 bg-gray-800 text-white">
        <span class="text-xs font-mono font-semibold">STEP 5: MINIMUM SAVINGS RATE</span>
      </div>
      <div class="p-4 font-mono text-sm">
        <div class="text-xs text-gray-500 mb-2">{{ m.msr_calculation.formula }}</div>
        <table class="w-full">
          <tbody>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 w-48">Total Benes</td><td class="py-1.5">{{ m.msr_calculation.total_benes?.toLocaleString() }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">MSR Threshold</td><td class="py-1.5 font-semibold">{{ (m.msr_calculation.msr * 100).toFixed(2) }}%</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Actual Savings %</td><td class="py-1.5">{{ (m.savings_calculation.savings_pct * 100).toFixed(2) }}%</td></tr>
            <tr>
              <td class="py-1.5 text-gray-500">Exceeds MSR?</td>
              <td class="py-1.5 font-bold" :class="m.msr_calculation.exceeds_msr ? 'text-emerald-600' : 'text-red-500'">
                {{ m.msr_calculation.exceeds_msr ? 'YES — eligible for shared savings' : 'NO — no shared savings' }}
              </td>
            </tr>
          </tbody>
        </table>
        <div class="mt-3">
          <span class="text-gray-500">Breakeven PMPY:</span>
          <span class="font-semibold">${{ fmtExact(projection.projection?.breakeven_pmpy) }}</span>
          <span class="text-gray-500 ml-2">(need to reduce PMPY by ${{ fmtExact(Math.abs(projection.projection?.pmpy_vs_breakeven || 0)) }} to reach MSR)</span>
        </div>
      </div>
    </div>

    <!-- Step 6: Track Application -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-2 border-b border-gray-200 bg-gray-800 text-white">
        <span class="text-xs font-mono font-semibold">STEP 6: TRACK APPLICATION ({{ projection.projection?.quality_met ? 'quality met' : 'quality FAILED' }})</span>
      </div>
      <div class="p-4 font-mono text-sm">
        <table class="w-full">
          <tbody>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500 w-48">Sharing Rate</td><td class="py-1.5">{{ (m.track_config.sharing_rate * 100) }}%</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Savings Cap</td><td class="py-1.5">{{ (m.track_config.savings_cap * 100) }}% of benchmark = ${{ fmtExact((m.track_config.savings_cap || 0) * (projection.benchmark?.total_benchmark || 0)) }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Loss Rate</td><td class="py-1.5">{{ (m.track_config.loss_rate * 100) }}%{{ m.track_config.loss_rate === 0 ? ' (no downside risk)' : '' }}</td></tr>
            <tr class="border-b border-gray-200"><td class="py-1.5 text-gray-500">Loss Cap</td><td class="py-1.5">{{ (m.track_config.loss_cap * 100) }}% of benchmark = ${{ fmtExact((m.track_config.loss_cap || 0) * (projection.benchmark?.total_benchmark || 0)) }}</td></tr>
            <tr v-if="projection.projection?.loss_exposure > 0" class="bg-red-50">
              <td class="py-1.5 text-red-700 font-semibold">Loss Exposure</td>
              <td class="py-1.5 text-red-700 font-semibold">
                ${{ fmtExact(projection.projection?.loss_exposure) }}
                <span class="font-normal text-xs ml-2">min(|${{ fmtExact(projection.projection?.savings_amount) }}| × {{ (m.track_config.loss_rate * 100) }}%, cap ${{ fmtExact((m.track_config.loss_cap || 0) * (projection.benchmark?.total_benchmark || 0)) }})</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Known Simplifications -->
    <div class="bg-white rounded-lg border border-amber-200 overflow-hidden">
      <div class="px-4 py-2 border-b border-amber-200 bg-amber-50">
        <span class="text-xs font-semibold text-amber-800 uppercase tracking-wide">KNOWN SIMPLIFICATIONS</span>
      </div>
      <div class="p-4">
        <ul class="space-y-2 text-sm">
          <li v-for="(note, idx) in m.simplifications" :key="idx" class="flex gap-2">
            <span class="text-amber-500 shrink-0">▸</span>
            <span class="text-gray-500">{{ note }}</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Raw JSON -->
    <details class="text-xs text-gray-500">
      <summary class="cursor-pointer hover:text-gray-900 py-2">Raw projection JSON</summary>
      <pre class="bg-gray-50 rounded p-3 overflow-auto max-h-96 font-mono text-xs mt-1">{{ JSON.stringify(projection, null, 2) }}</pre>
    </details>
  </div>
</template>

<script>
export default {
  name: 'AcoModelView',
  props: {
    projection: { type: Object, required: true },
  },
  computed: {
    m() { return this.projection?._model },
    enrollmentTypes() {
      const bt = this.m?.regional_ffs?.benchmark_by_enrollment_type
      const mix = this.projection?.benchmark?.enrollment_mix
      if (!bt) return null
      return {
        'ESRD': { ...bt.esrd, mix: mix?.pct_esrd },
        'Disabled': { ...bt.disabled, mix: mix?.pct_disabled },
        'Aged/Dual': { ...bt.aged_dual, mix: mix?.pct_aged_dual },
        'Aged/Non-Dual': { ...bt.aged_nondual, mix: mix?.pct_aged_nondual },
      }
    },
  },
  methods: {
    fmtNum(val) {
      if (val == null) return '—'
      if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M'
      if (val >= 1000) return (val / 1000).toFixed(1) + 'K'
      return val.toLocaleString()
    },
    fmtExact(val) {
      if (val == null) return '—'
      return Math.round(val).toLocaleString()
    },
    fmtPct(val) {
      if (val == null) return '—'
      return (val * 100).toFixed(1) + '%'
    },
  },
}
</script>
